<template>
  <div class="contract-detail" v-loading="loading">
    <div class="page-header">
      <h2>계약서 상세</h2>
      <div class="header-actions">
        <el-button @click="$router.push('/contract')">목록</el-button>
        <el-button v-if="detail" :icon="View" @click="previewVisible = true">직원 화면 미리보기</el-button>
        <el-button
          v-if="detail?.status === 'DRAFT'"
          type="primary"
          @click="handleDistribute"
          :loading="distributing"
        >
          배포하기
        </el-button>
        <el-button
          v-if="detail?.status === 'DISTRIBUTED' || detail?.status === 'IN_PROGRESS'"
          type="warning"
          @click="handleRemind"
        >
          독촉 발송
        </el-button>
        <el-button
          v-if="detail?.status !== 'DRAFT'"
          type="success"
          @click="handleExportExcel"
        >
          Excel 다운로드
        </el-button>
        <el-button
          v-if="detail?.status === 'DRAFT'"
          type="danger"
          @click="handleCancel"
        >
          취소
        </el-button>
        <el-tooltip v-if="detail?.deletable" :content="detail.delete_needs_confirm ? '배포된 계약서 — 동의 기록·완성본까지 영구 삭제 (시스템 관리자 전용, 테스트 건 정리용)' : '작성중·취소 건은 기록 없이 완전히 지웁니다'" placement="bottom">
          <el-button type="danger" plain @click="handleDelete">삭제</el-button>
        </el-tooltip>
        <el-button @click="helpVisible = true">
          <el-icon><QuestionFilled /></el-icon>
          도움말
        </el-button>
      </div>
    </div>

    <template v-if="detail">
      <!-- 진행 흐름: 현재 단계 + 다음 할 일(누가·무엇) -->
      <el-card class="info-card">
        <ContractFlowSteps mode="admin" :detail="detail" :missing-files="individualFileMissing" />
      </el-card>

      <!-- 기본 정보 -->
      <el-card class="info-card">
        <el-descriptions :column="2" border>
          <el-descriptions-item label="계약번호">{{ detail.contract_number }}</el-descriptions-item>
          <el-descriptions-item label="상태">
            <el-tag :type="statusTagType(detail.status)">{{ detail.status_display }}</el-tag>
          </el-descriptions-item>
          <el-descriptions-item label="계약서명">{{ detail.title }}</el-descriptions-item>
          <el-descriptions-item label="분류">{{ detail.template_category_display }}</el-descriptions-item>
          <el-descriptions-item label="배포유형">
            <el-tag :type="detail.distribute_type === 'INDIVIDUAL' ? 'warning' : 'info'" size="small">
              {{ detail.distribute_type === 'INDIVIDUAL' ? '개별 (직원별 다른 문서)' : '공통 (전원 동일 문서)' }}
            </el-tag>
          </el-descriptions-item>
          <el-descriptions-item v-if="detail.contract_start_date" label="계약 시작일">
            {{ detail.contract_start_date }}
            <span class="muted" style="margin-left:6px">(근로조건 기준일)</span>
          </el-descriptions-item>
          <el-descriptions-item label="확인기한">
            <span :style="{ color: detail.is_overdue ? '#F56C6C' : '' }">
              {{ detail.deadline?.substring(0, 16) }}
              <template v-if="detail.days_until_deadline != null">
                ({{ detail.days_until_deadline > 0 ? `D-${detail.days_until_deadline}` : detail.days_until_deadline === 0 ? 'D-Day' : `D+${Math.abs(detail.days_until_deadline)}` }})
              </template>
            </span>
          </el-descriptions-item>
          <el-descriptions-item label="배포일">{{ detail.distribute_date?.substring(0, 16) || '-' }}</el-descriptions-item>
          <el-descriptions-item label="공통파일">
            <el-link v-if="detail.document_file" :href="detail.document_file" target="_blank" type="primary">다운로드</el-link>
            <span v-else>{{ detail.distribute_type === 'INDIVIDUAL' ? '개별 배포 (공통파일 없음)' : '-' }}</span>
          </el-descriptions-item>
          <el-descriptions-item label="메모" :span="2">{{ detail.memo || '-' }}</el-descriptions-item>
        </el-descriptions>
      </el-card>

      <!-- 진행 현황 -->
      <el-card class="info-card">
        <template #header><h3>진행 현황</h3></template>
        <div class="progress-summary">
          <div class="progress-bar-wrap">
            <el-progress :percentage="detail.consent_rate" :stroke-width="20" />
            <span class="progress-text">
              {{ detail.parties_summary?.consented ?? detail.consented_count }} / {{ detail.parties_summary?.total ?? detail.total_parties }}명 동의
              ({{ (detail.parties_summary?.total ?? detail.total_parties) - (detail.parties_summary?.consented ?? detail.consented_count) }}명 미동의)
            </span>
          </div>
          <div class="status-chips" v-if="detail.parties_summary">
            <el-tag type="info" size="small">미확인 {{ detail.parties_summary.pending }}</el-tag>
            <el-tag type="warning" size="small">열람중 {{ detail.parties_summary.read }}</el-tag>
            <el-tag type="success" size="small">동의 {{ detail.parties_summary.consented }}</el-tag>
            <el-tag type="danger" size="small">거부 {{ detail.parties_summary.rejected }}</el-tag>
            <el-tag size="small">만료 {{ detail.parties_summary.expired }}</el-tag>
          </div>
        </div>
      </el-card>

      <!-- 대상자 목록 -->
      <el-card class="info-card">
        <template #header>
          <div style="display:flex;justify-content:space-between;align-items:center;flex-wrap:wrap;gap:8px;">
            <h3>대상자 목록</h3>
            <div v-if="(hasTermFields || detail.template_has_body) && termsEditable" class="terms-toolbar">
              <template v-if="hasTermFields">
                <el-button size="small" :icon="Download" @click="downloadTermsExcel">조건 엑셀 내려받기</el-button>
                <el-upload :auto-upload="false" :show-file-list="false" accept=".xlsx" :on-change="(f) => handleTermsExcel(f)">
                  <el-button size="small" :icon="Upload">조건 엑셀 올리기</el-button>
                </el-upload>
                <el-button v-if="detail.template_uses_master" size="small" type="warning" plain :icon="Refresh" :loading="prefilling" @click="handlePrefillMaster">
                  근로조건 마스터에서 다시 가져오기
                </el-button>
              </template>
              <el-button v-if="detail.template_has_body" size="small" type="primary" plain :icon="Document" :loading="generating" @click="handleGenerateAll">
                {{ hasTermFields ? '계약서 문서 생성 (조건 입력된 전원)' : '문서 다시 생성 (전원)' }}
              </el-button>
            </div>
            <el-select v-model="partyStatusFilter" clearable placeholder="전체 상태" size="small" style="width:140px" @change="fetchParties">
              <el-option label="미확인" value="PENDING" />
              <el-option label="열람중" value="READ" />
              <el-option label="동의완료" value="CONSENTED" />
              <el-option label="거부" value="REJECTED" />
              <el-option label="만료" value="EXPIRED" />
            </el-select>
          </div>
        </template>
        <el-table :data="contractStore.parties" size="small">
          <el-table-column prop="employee_name" label="이름" width="120" />
          <el-table-column prop="department_name" label="부서" width="120" />
          <el-table-column prop="status_display" label="상태" width="100">
            <template #default="{ row }">
              <el-tag :type="partyStatusTag(row.status)" size="small">{{ row.status_display }}</el-tag>
            </template>
          </el-table-column>
          <el-table-column label="사유 / 조치 / 완성본" min-width="260">
            <template #default="{ row }">
              <template v-if="row.status === 'REJECTED'">
                <div class="reject-reason">{{ row.rejection_reason || '(사유 없음)' }}</div>
                <el-button
                  v-if="['DISTRIBUTED', 'IN_PROGRESS'].includes(detail.status)"
                  size="small" type="warning" plain :icon="RefreshRight"
                  @click="handleReRequest(row)"
                >재요청</el-button>
              </template>
              <template v-else-if="row.status === 'EXPIRED' && ['DISTRIBUTED', 'IN_PROGRESS'].includes(detail.status)">
                <el-button size="small" plain :icon="RefreshRight" @click="handleReRequest(row)">재요청</el-button>
              </template>
              <template v-else-if="row.status === 'CONSENTED'">
                <el-button v-if="row.has_signed_document" size="small" type="success" plain :icon="Download" @click="downloadSigned(row)">
                  서명 완성본
                </el-button>
                <span v-else class="muted">완성본 생성 중/없음</span>
              </template>
              <span v-else class="muted">-</span>
            </template>
          </el-table-column>
          <!-- 계약 조건 컬럼 (양식에 항목이 있을 때) -->
          <el-table-column v-if="hasTermFields" label="계약 조건" min-width="220">
            <template #default="{ row }">
              <div v-if="row.terms_summary" class="terms-summary" :class="{ incomplete: !row.terms_complete }">{{ row.terms_summary }}</div>
              <span v-else class="muted">미입력</span>
              <el-button
                v-if="termsEditable && row.status !== 'CONSENTED'"
                size="small" link type="primary" :icon="Edit" @click="openTerms(row)"
              >{{ row.terms_summary ? '수정' : '입력' }}</el-button>
              <div v-if="!row.terms_complete && row.terms_errors?.length" class="terms-errors">
                누락: {{ row.terms_errors.map((e) => e.replace(/은\(는\) 필수입니다\./, '')).join(', ') }}
              </div>
            </template>
          </el-table-column>

          <!-- 개별 배포: 파일 업로드 컬럼 -->
          <el-table-column v-if="detail.distribute_type === 'INDIVIDUAL'" label="개별파일" width="200">
            <template #default="{ row }">
              <el-link v-if="row.individual_file" :href="row.individual_file" target="_blank" type="primary" size="small">파일보기</el-link>
              <span v-if="row.individual_file && detail.template_has_body" class="muted" style="margin-left:6px">자동 생성</span>
              <!-- 자동 생성 양식에서는 수동 업로드/교체를 막는다 — 조건을 고치면 문서가 다시 만들어지므로, 손으로 바꾼 PDF는 조건과 어긋나고 재생성 시 덮어써진다 -->
              <el-upload
                v-if="canUploadFor(row) && !detail.template_has_body"
                :auto-upload="false"
                :show-file-list="false"
                accept=".pdf,.docx"
                :on-change="(file) => handleIndividualUpload(row, file)"
                style="display:inline-block; margin-left:8px"
              >
                <el-button size="small" :type="row.individual_file ? 'default' : 'primary'" :plain="!!row.individual_file" :icon="Upload">
                  {{ row.individual_file ? '교체' : '파일 업로드' }}
                </el-button>
              </el-upload>
            </template>
          </el-table-column>
          <el-table-column prop="first_read_at" label="최초열람" width="140">
            <template #default="{ row }">{{ row.first_read_at?.substring(0, 16) || '-' }}</template>
          </el-table-column>
          <el-table-column prop="total_read_seconds" label="열람시간" width="90">
            <template #default="{ row }">{{ row.total_read_seconds }}초</template>
          </el-table-column>
          <el-table-column prop="consented_at" label="동의일시" width="140">
            <template #default="{ row }">{{ row.consented_at?.substring(0, 16) || '-' }}</template>
          </el-table-column>
          <el-table-column prop="reminder_sent_count" label="독촉" width="70" />
        </el-table>

        <!-- 개별 배포 안내 -->
        <el-alert
          v-if="detail.distribute_type === 'INDIVIDUAL' && individualFileMissing > 0"
          type="warning"
          :closable="false"
          show-icon
          style="margin-top:12px"
        >
          <template v-if="detail.template_has_body && !hasTermFields">
            {{ individualFileMissing }}명의 문서가 아직 생성되지 않았습니다. 위 [문서 다시 생성 (전원)] 버튼을 누르면 서식으로 PDF가 만들어집니다.
          </template>
          <template v-else-if="detail.template_has_body">
            {{ individualFileMissing }}명의 계약서가 아직 생성되지 않았습니다. "계약 조건" 열에서 값을 입력하면(또는 엑셀로 올리면) 계약서 PDF가 자동 생성됩니다.
          </template>
          <template v-else>
            {{ individualFileMissing }}명의 개별 파일이 아직 없습니다. 위 대상자 목록의 "개별파일" 열에서 [파일 업로드] 버튼으로 직원별 계약서(PDF/DOCX)를 올린 뒤 배포하세요.
          </template>
        </el-alert>
      </el-card>
    </template>

    <!-- 계약 조건 입력 -->
    <PartyTermsDialog
      v-if="detail"
      v-model="termsDialogVisible"
      :contract-id="route.params.id"
      :party="termsParty"
      :fields="detail.template_term_fields || []"
      :has-body="!!detail.template_has_body"
      @saved="afterTermsSaved"
    />

    <!-- 직원 화면 미리보기 -->
    <ContractEmployeePreview v-if="detail" v-model="previewVisible" :contract="detail" :parties="contractStore.parties || []" />

    <!-- 도움말 패널 -->
    <HelpPanel v-model="helpVisible" :content-path="'screens/contract-detail'" />
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import { ElMessage, ElMessageBox } from "element-plus";
import { useContractStore } from "@/stores/contractStore";
import { QuestionFilled, Upload, View, RefreshRight, Download, Edit, Document, Refresh } from "@element-plus/icons-vue";
import PartyTermsDialog from "@/components/contract/PartyTermsDialog.vue";
import axiosInstance from "@/api/axios";
import ContractEmployeePreview from "@/components/contract/ContractEmployeePreview.vue";
import HelpPanel from "@/components/help/HelpPanel.vue";
import ContractFlowSteps from "@/components/contract/ContractFlowSteps.vue";

const route = useRoute();
const router = useRouter();
const contractStore = useContractStore();

const helpVisible = ref(false);
const previewVisible = ref(false);
const loading = ref(false);
const distributing = ref(false);
const detail = ref(null);
const partyStatusFilter = ref("");

const individualFileMissing = computed(() => {
  if (!contractStore.parties) return 0;
  return contractStore.parties.filter((p) => !p.individual_file).length;
});

onMounted(async () => {
  loading.value = true;
  try {
    detail.value = await contractStore.fetchContractDetail(route.params.id);
    await fetchParties();
  } finally {
    loading.value = false;
  }
});

async function fetchParties() {
  const params = {};
  if (partyStatusFilter.value) params.status = partyStatusFilter.value;
  await contractStore.fetchParties(route.params.id, params);
}

async function handleDistribute() {
  // 개별 배포인데 파일이 없는 대상자가 있으면 경고
  if (detail.value.distribute_type === "INDIVIDUAL" && individualFileMissing.value > 0) {
    try {
      await ElMessageBox.confirm(
        `${individualFileMissing.value}명의 개별 파일이 아직 없습니다. 그래도 배포하시겠습니까?`,
        "미완료 파일 있음",
        { type: "warning" }
      );
    } catch {
      return;
    }
  } else {
    try {
      await ElMessageBox.confirm(
        "직원 화면 미리보기로 문서·동의 문구를 확인하셨나요? 배포 후에는 파일·대상자·기한을 수정할 수 없습니다.",
        "배포 확인"
      );
    } catch {
      return;
    }
  }

  distributing.value = true;
  try {
    const res = await contractStore.distributeContract(route.params.id);
    ElMessage.success(res.message);
    detail.value = await contractStore.fetchContractDetail(route.params.id);
    await fetchParties();
  } catch (e) {
    ElMessage.error(e.response?.data?.error || "배포 실패");
  } finally {
    distributing.value = false;
  }
}

async function handleRemind() {
  try {
    await ElMessageBox.confirm("미확인 대상자에게 독촉 알림을 발송하시겠습니까?", "독촉 발송");
    const res = await contractStore.remindContract(route.params.id);
    ElMessage.success(res.message);
  } catch (e) {
    if (e !== "cancel") ElMessage.error("독촉 발송 실패");
  }
}

async function handleExportExcel() {
  try {
    await contractStore.exportExcel(route.params.id);
    ElMessage.success("Excel 파일이 다운로드되었습니다.");
  } catch {
    ElMessage.error("Excel 다운로드 실패");
  }
}

async function handleCancel() {
  try {
    const { value: reason } = await ElMessageBox.prompt("취소 사유를 입력해주세요.", "계약서 취소", {
      confirmButtonText: "취소 확인",
      cancelButtonText: "돌아가기",
    });
    await contractStore.cancelContract(route.params.id, reason);
    ElMessage.success("계약서가 취소되었습니다.");
    detail.value = await contractStore.fetchContractDetail(route.params.id);
  } catch (e) {
    if (e !== "cancel") ElMessage.error("취소 실패");
  }
}

// 영구 삭제 — 작성중·취소는 확인만, 배포된 건은 계약번호를 직접 입력해야 진행 (오삭제 방지)
async function handleDelete() {
  const d = detail.value;
  try {
    if (d.delete_needs_confirm) {
      await ElMessageBox.prompt(
        `이 계약서는 이미 배포되어 직원의 열람·동의 기록과 서명 완성본이 있습니다. 삭제하면 증빙이 모두 사라지며 되돌릴 수 없습니다.\n\n정말 지우려면 계약번호 ${d.contract_number} 를 그대로 입력하세요.`,
        "배포된 계약서 영구 삭제",
        { confirmButtonText: "영구 삭제", cancelButtonText: "돌아가기", type: "error",
          inputValidator: (v) => v === d.contract_number || "계약번호가 일치하지 않습니다", inputPlaceholder: d.contract_number }
      );
    } else {
      await ElMessageBox.confirm(`${d.contract_number} ${d.title} 을(를) 삭제합니다. 대상자 목록과 만들어진 문서가 함께 지워지며 되돌릴 수 없습니다.`, "계약서 삭제", { type: "warning", confirmButtonText: "삭제", cancelButtonText: "돌아가기" });
    }
    await contractStore.deleteContract(d.id);
    ElMessage.success("계약서가 삭제되었습니다.");
    router.push("/contract");
  } catch (e) {
    if (e !== "cancel") ElMessage.error("삭제 실패: " + (e.response?.data?.detail || e.message));
  }
}

// 개별파일 업로드/교체 가능 조건: 계약서가 종료 상태가 아니고, 대상자가 아직 동의 전
function canUploadFor(row) {
  const closed = ["COMPLETED", "CANCELLED", "EXPIRED"].includes(detail.value?.status);
  return !closed && row.status !== "CONSENTED";
}

async function handleIndividualUpload(party, file) {
  try {
    const res = await contractStore.uploadIndividualFile(route.params.id, party.id, file.raw);
    ElMessage.success(res.message);
    await fetchParties();
  } catch (e) {
    ElMessage.error("파일 업로드 실패: " + (e.response?.data?.error || e.message));
  }
}

// ===== 계약 조건 =====
const termsDialogVisible = ref(false);
const termsParty = ref(null);
const generating = ref(false);
const hasTermFields = computed(() => (detail.value?.template_term_fields || []).length > 0);
const termsEditable = computed(() => ["DRAFT", "DISTRIBUTED", "IN_PROGRESS"].includes(detail.value?.status));

function openTerms(row) { termsParty.value = row; termsDialogVisible.value = true; }
async function afterTermsSaved() {
  await fetchParties();
  detail.value = await contractStore.fetchContractDetail(route.params.id);
}
async function handleGenerateAll() {
  generating.value = true;
  try {
    const r = await contractStore.generateDocuments(route.params.id);
    ElMessage.success(r.message);
    if (r.errors?.length) {
      await ElMessageBox.alert(
        r.errors.map((e) => `${e.name}: ${e.errors.join(", ")}`).join("\n"),
        "생성하지 못한 대상자", { confirmButtonText: "확인" }
      );
    }
    await afterTermsSaved();
  } catch (e) {
    ElMessage.error("생성 실패: " + (e.response?.data?.error || e.message));
  } finally {
    generating.value = false;
  }
}
const prefilling = ref(false);
async function handlePrefillMaster() {
  try {
    await ElMessageBox.confirm(
      "근로조건 관리에 등록된 각 직원의 현재 조건으로 이 계약서의 조건을 다시 채우고(기존 값 덮어씀), 서식이 있으면 문서를 다시 만듭니다. 이미 동의한 직원은 제외됩니다.",
      "마스터에서 가져오기", { type: "warning", confirmButtonText: "가져오기" }
    );
    prefilling.value = true;
    const r = await axiosInstance.post(`/contracts/${route.params.id}/prefill-from-master/`, null, { params: { overwrite: 1 } });
    ElMessage.success(r.data.message);
    if (r.data.missing?.length) ElMessage.warning("근로조건 미등록: " + r.data.missing.join(", ") + " — 근로조건 관리에서 먼저 등록하세요.");
    await afterTermsSaved();
  } catch (e) {
    if (e !== "cancel") ElMessage.error("가져오기 실패: " + (e.response?.data?.error || e.message));
  } finally {
    prefilling.value = false;
  }
}

async function downloadTermsExcel() {
  try {
    await contractStore.downloadTermsExcel(route.params.id, `${detail.value.contract_number}_계약조건.xlsx`);
  } catch (e) {
    ElMessage.error("내려받기 실패: " + (e.response?.data?.error || e.message));
  }
}
async function handleTermsExcel(file) {
  try {
    const dry = await contractStore.uploadTermsExcel(route.params.id, file.raw, false);
    const errText = (dry.errors || []).map((e) => `${e.row}행 ${e.name}: ${e.messages.join(", ")}`).join("\n");
    if (dry.errors?.length) {
      await ElMessageBox.alert(`${dry.message}\n\n${errText}`, "검증 결과 — 반영되지 않음", { confirmButtonText: "확인" });
      return;
    }
    await ElMessageBox.confirm(`${dry.message}\n반영할까요?`, "검증 완료", { type: "info", confirmButtonText: "반영", cancelButtonText: "취소" });
    const ap = await contractStore.uploadTermsExcel(route.params.id, file.raw, true);
    ElMessage.success(ap.message);
    await afterTermsSaved();
  } catch (e) {
    if (e !== "cancel") ElMessage.error("엑셀 처리 실패: " + (e.response?.data?.error || e.message));
  }
}

async function downloadSigned(party) {
  const name = party.employee_name || party.employee_username;
  try {
    await contractStore.downloadSignedDocument(
      `/contracts/${route.params.id}/signed-document/?party_id=${party.id}`,
      `${detail.value.contract_number}_${name}_서명완성본.pdf`
    );
  } catch (e) {
    ElMessage.error("내려받기 실패: " + (e.response?.data?.error || e.message));
  }
}

async function handleReRequest(party) {
  const name = party.employee_name || party.employee_username;
  try {
    const { value: note } = await ElMessageBox.prompt(
      `${name}님에게 다시 확인을 요청합니다. 거부 사유를 반영해 문서를 수정했다면 먼저 "개별파일 교체"로 올린 뒤 진행하세요.\n직원에게 전달할 메모(선택):`,
      "재요청",
      { confirmButtonText: "재요청 보내기", cancelButtonText: "취소", inputType: "textarea", inputPlaceholder: "예: 연봉 항목 정정했습니다" }
    );
    const res = await contractStore.reRequestParty(route.params.id, party.id, note || "");
    ElMessage.success(res.message);
    detail.value = await contractStore.fetchContractDetail(route.params.id);
    await fetchParties();
  } catch (e) {
    if (e !== "cancel") ElMessage.error("재요청 실패: " + (e.response?.data?.error || e.message));
  }
}

function statusTagType(s) {
  return { DRAFT: "info", DISTRIBUTED: "warning", IN_PROGRESS: "", COMPLETED: "success", EXPIRED: "danger", CANCELLED: "info" }[s] || "";
}

function partyStatusTag(s) {
  return { PENDING: "info", READ: "warning", CONSENTED: "success", REJECTED: "danger", EXPIRED: "" }[s] || "";
}
</script>

<style scoped>
.page-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 16px; }
.info-card { margin-bottom: 16px; }
.info-card h3 { margin: 0; font-size: 15px; }
.progress-summary { text-align: center; }
.progress-bar-wrap { max-width: 500px; margin: 0 auto 12px; }
.progress-text { font-size: 14px; color: #666; }
.status-chips { display: flex; gap: 8px; justify-content: center; }
.terms-toolbar { display: flex; align-items: center; gap: var(--sw-space-2); flex-wrap: wrap; }
.terms-summary { font-size: var(--sw-font-sm); }
.terms-summary.incomplete { color: var(--el-color-danger); }
.terms-errors { font-size: var(--sw-font-xs); color: var(--el-color-danger); }
.reject-reason { color: var(--el-color-danger); font-size: var(--sw-font-sm); white-space: pre-wrap; margin-bottom: var(--sw-space-1); }
.muted { color: var(--el-text-color-placeholder); }
</style>
